import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getNotificationSettings, updateNotificationSettings } from '../../../services/userService';

function Notification() {
  const [settings, setSettings] = useState({
    emailNotifications: false,
    pushNotifications: false,
    invoiceEmails: false,
  });

  const [isSaving, setIsSaving] = useState(false); // ✅ NEW: control spinner + disable

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      const response = await getNotificationSettings();
      if (response?.success && response?.data) {
        const data = response.data;
        setSettings({
          emailNotifications: data.mail_notification === 1,
          pushNotifications: data.push_notification === 1,
          invoiceEmails: data.invoice_mail === 1,
        });
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast.error('Failed to load notification settings');
    }
  };

  const handleSave = async () => {
    setIsSaving(true); // Show spinner
    try {
      const payload = {
        mail_notification: settings.emailNotifications ? 1 : 0,
        push_notification: settings.pushNotifications ? 1 : 0,
        invoice_mail: settings.invoiceEmails ? 1 : 0,
      };

      // Simulate delay
      setTimeout(async () => {
        const response = await updateNotificationSettings(payload);
        if (response.success) {
          toast.success(response.data.message || 'Preferences updated successfully!');
        } else {
          toast.error(response.data.message || 'Failed to update preferences.');
        }
        setIsSaving(false); // Hide spinner
      }, 2000);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('An error occurred while updating settings.');
      setIsSaving(false); // Hide spinner
    }
  };

  return (
    <div className="p-4">
      <div>
        <h4 className="text-xl font-semibold mb-2">Notification Channels</h4>
        <p className="text-gray-500 mb-6">Choose how you want to receive notifications</p>

        <div className="space-y-4">
          {/* Email */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Icon icon="mdi:email-outline" width="20" height="20" className="text-gray-600" />
              </div>
              <div>
                <h5 className="font-semibold">Email Notifications</h5>
                <p className="text-sm text-gray-500">Receive notifications via email</p>
              </div>
            </div>
            <div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>

          {/* Push */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Icon icon="mdi:bell-outline" width="20" height="20" className="text-gray-600" />
              </div>
              <div>
                <h5 className="font-semibold">Push Notifications</h5>
                <p className="text-sm text-gray-500">Receive notifications in your browser</p>
              </div>
            </div>
            <div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.pushNotifications}
                  onChange={() => handleToggle('pushNotifications')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>

          {/* Invoice Email */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Icon icon="mdi:file-document-outline" width="20" height="20" className="text-gray-600" />
              </div>
              <div>
                <h5 className="font-semibold">Invoice Emails</h5>
                <p className="text-sm text-gray-500">Receive invoice updates via email</p>
              </div>
            </div>
            <div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.invoiceEmails}
                  onChange={() => handleToggle('invoiceEmails')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <div className="w-full md:w-1/4">
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Notification;
